import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import translations from "@/translations";
import {
  MapPin,
  Phone,
  Mail,
  ArrowRight,
  Building2,
  Users,
  Presentation,
  Landmark,
  Clock,
  Wifi,
  Coffee,
  Shield,
  ChevronDown,
  Menu,
  X,
  Quote,
  Globe,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LangContext = createContext({ lang: "fr", t: translations.fr, toggle: () => {} });
function useLang() { return useContext(LangContext); }

const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/4d7aaa54-3f40-4955-9ac4-09a55ec1cb99/images/2da21f13f6b350eb01233c7c74c1f7146aaa68f14d680cbcd290df9f52e34aa3.png",
  office: "https://images.pexels.com/photos/5511088/pexels-photo-5511088.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  coworking: "https://images.pexels.com/photos/3778613/pexels-photo-3778613.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  meeting: "https://images.pexels.com/photos/1181304/pexels-photo-1181304.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  texture: "https://images.unsplash.com/photo-1565262353342-6e919eab5b58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjBidWlsZGluZyUyMGVudHJhbmNlJTIwcmVjZXB0aW9uJTIwbG9iYnl8ZW58MHx8fHwxNzc0OTY3MDA2fDA&ixlib=rb-4.1.0&q=85",
};

const SERVICE_META = [
  { id: "private-offices", icon: Building2, image: IMAGES.office, span: "md:col-span-7", height: "h-[420px]" },
  { id: "coworking", icon: Users, image: IMAGES.coworking, span: "md:col-span-5", height: "h-[420px]" },
  { id: "meeting-rooms", icon: Presentation, image: IMAGES.meeting, span: "md:col-span-5", height: "h-[380px]" },
  { id: "domiciliation", icon: Landmark, image: IMAGES.texture, span: "md:col-span-7", height: "h-[380px]" },
];

const FEATURE_ICONS = [Clock, Wifi, Coffee, Shield];

const TESTIMONIALS = [
  { text_fr: "Un espace de travail bien situ\u00e9 et un cabinet comptable juste \u00e0 c\u00f4t\u00e9, c'est exactement ce qu'il nous fallait pour nous installer au Luxembourg.", text_en: "A well-located workspace and an accounting firm right next door is exactly what we needed to set up in Luxembourg.", author: "Sophie M.", role: "Fondatrice, startup fintech", avatar: "https://api.dicebear.com/9.x/notionists/svg?seed=Sophie&backgroundColor=f5f0eb" },
  { text_fr: "Le fait d'avoir un accompagnement comptable sur place, c'est un vrai plus. On gagne un temps fou.", text_en: "Having accounting support on-site is a real bonus. It saves us so much time.", author: "Marc D.", role: "G\u00e9rant, soci\u00e9t\u00e9 de conseil", avatar: "https://api.dicebear.com/9.x/notionists/svg?seed=Marc&backgroundColor=f5f0eb" },
  { text_fr: "Je cherchais un bureau et un comptable de confiance. Ici j'ai trouv\u00e9 les deux, et en plus le cadre est agr\u00e9able.", text_en: "I was looking for an office and a reliable accountant. Here I found both, and the setting is pleasant too.", author: "Laurent K.", role: "Ind\u00e9pendant, consultant IT", avatar: "https://api.dicebear.com/9.x/notionists/svg?seed=Laurent&backgroundColor=f5f0eb" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

function AnimatedSection({ children, className = "" }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={isInView ? "visible" : "hidden"} variants={staggerContainer} className={className}>
      {children}
    </motion.div>
  );
}

function LangToggle() {
  const { lang, toggle } = useLang();
  return (
    <button
      data-testid="lang-toggle"
      onClick={toggle}
      className="flex items-center gap-2 font-mono-accent text-[#666666] hover:text-[#111111] transition-colors duration-300"
    >
      <Globe size={14} strokeWidth={1.5} />
      {lang === "fr" ? "EN" : "FR"}
    </button>
  );
}

function Navigation() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const navItems = [
    { label: t.nav.services, id: "services" },
    { label: t.nav.espace, id: "about" },
    { label: t.nav.localisation, id: "location" },
  ];

  return (
    <nav data-testid="navigation-bar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "nav-glass" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <a data-testid="nav-logo" href="#hero" onClick={(e) => { e.preventDefault(); scrollTo("hero"); }} className="flex items-center gap-3">
          <span className="text-lg md:text-xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Crown Workspace</span>
          <span className="font-mono-accent text-[0.75rem] text-[#8B7650] font-medium hidden sm:inline">Luxembourg</span>
        </a>
        <div className="hidden md:flex items-center gap-10">
          {navItems.map((item) => (
            <button key={item.id} data-testid={`nav-link-${item.id}`} onClick={() => scrollTo(item.id)} className="font-mono-accent text-[#666666] hover:text-[#111111] transition-colors duration-300">
              {item.label}
            </button>
          ))}
          <LangToggle />
          <button data-testid="nav-cta-button" onClick={() => scrollTo("contact")} className="btn-sharp text-[0.65rem] py-3 px-6">
            {t.nav.cta}
          </button>
        </div>
        <button data-testid="mobile-menu-toggle" className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X strokeWidth={1.5} size={22} /> : <Menu strokeWidth={1.5} size={22} />}
        </button>
      </div>
      {mobileOpen && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden bg-white border-t border-black/5 px-6 py-8 flex flex-col gap-6">
          {[...navItems, { label: "Contact", id: "contact" }].map((item) => (
            <button key={item.id} data-testid={`mobile-nav-link-${item.id}`} onClick={() => scrollTo(item.id)} className="font-mono-accent text-left text-[#666666]">
              {item.label}
            </button>
          ))}
          <LangToggle />
          <button data-testid="mobile-nav-cta" onClick={() => scrollTo("contact")} className="btn-sharp w-full text-center">
            {t.nav.cta}
          </button>
        </motion.div>
      )}
    </nav>
  );
}

function HeroSection() {
  const { t } = useLang();
  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end">
      <div className="absolute inset-0">
        <img src={IMAGES.hero} alt="Crown Workspace Luxembourg" className="w-full h-full object-cover" />
        <div className="hero-gradient-overlay absolute inset-0" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12 pb-16 md:pb-24">
        <AnimatedSection>
          <motion.p variants={fadeUp} className="font-mono-accent text-[#B89B72] mb-4">{t.hero.address}</motion.p>
          <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl md:text-7xl leading-[1.05] tracking-tight max-w-4xl font-semibold">
            {t.hero.title1}<br /><span className="italic text-[#B89B72]">{t.hero.title2}</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-6 md:mt-8 text-base md:text-lg text-[#333333] max-w-xl leading-relaxed font-medium">
            {t.hero.subtitle}
          </motion.p>
          <motion.div variants={fadeUp} className="mt-8 md:mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-cta-button" onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })} className="btn-sharp flex items-center gap-3">
              {t.hero.ctaPrimary} <ArrowRight size={14} strokeWidth={1.5} />
            </button>
            <button data-testid="hero-services-button" onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })} className="btn-outline-sharp flex items-center gap-3">
              {t.hero.ctaSecondary} <ChevronDown size={14} strokeWidth={1.5} />
            </button>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

function MarqueeRibbon() {
  const { t } = useLang();
  return (
    <div data-testid="marquee-ribbon" className="section-border py-5 bg-[#1a1a1a]">
      <Marquee speed={30} gradient={false}>
        {t.marquee.map((text, i) => (<span key={i} className="marquee-text">{text} &bull;</span>))}
      </Marquee>
    </div>
  );
}

function ServicesSection() {
  const { lang, t } = useLang();
  return (
    <section id="services" data-testid="services-section" className="section-border py-20 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <AnimatedSection>
          <motion.p variants={fadeUp} className="font-mono-accent text-[#B89B72] mb-4">{t.services.label}</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl tracking-tight max-w-2xl">{t.services.title}</motion.h2>
          <motion.p variants={fadeUp} className="mt-4 text-[#666666] max-w-lg leading-relaxed font-light">{t.services.subtitle}</motion.p>
        </AnimatedSection>
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
          {SERVICE_META.map((service) => {
            const Icon = service.icon;
            const s = t.services.items[service.id];
            return (
              <div key={service.id} data-testid={`service-card-${service.id}`} className={`grid-card ${service.span} group`}>
                <div className={`img-hover-container ${service.height} relative`}>
                  <img src={service.image} alt={s.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <Icon size={18} strokeWidth={1.5} className="text-white/80" />
                      <span className="font-mono-accent text-white/60">{service.id}</span>
                    </div>
                    <h3 className="text-xl md:text-2xl text-white tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>{s.title}</h3>
                    <p className="mt-2 text-sm text-white/70 font-light leading-relaxed max-w-md">{s.desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-xs text-[#999999] font-light italic text-right">
          {lang === "fr" ? "* Photos non contractuelles, donn\u00e9es \u00e0 titre indicatif." : "* Photos are for illustrative purposes only."}
        </p>
      </div>
    </section>
  );
}

function FeaturesStrip() {
  const { t } = useLang();
  return (
    <div data-testid="features-strip" className="section-border bg-[#FAFAFA] py-12 md:py-16 px-6 md:px-12">
      <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
        {FEATURE_ICONS.map((Icon, i) => (
          <div key={i} data-testid={`feature-${i}`} className="flex items-center gap-4">
            <div className="w-12 h-12 border border-black/10 flex items-center justify-center">
              <Icon size={20} strokeWidth={1} className="text-[#B89B72]" />
            </div>
            <span className="font-mono-accent text-[#666666]">{t.features.items[i]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingSection() {
  const { lang } = useLang();

  return (
    <section id="pricing" data-testid="pricing-section" className="section-border py-20 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <AnimatedSection>
          <motion.p variants={fadeUp} className="font-mono-accent text-[#B89B72] mb-4">
            {lang === "fr" ? "Tarifs" : "Pricing"}
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl tracking-tight">
            {lang === "fr" ? "Nos formules" : "Our plans"}
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-4 text-[#666666] max-w-lg leading-relaxed font-light">
            {lang === "fr" ? "Des tarifs clairs, sans surprise. Tous les prix sont mensuels HT." : "Clear pricing, no surprises. All prices are monthly excl. VAT."}
          </motion.p>
        </AnimatedSection>

        {/* 4ème étage */}
        <div className="mt-12 md:mt-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-[#1a1a1a] flex items-center justify-center">
              <span className="text-white text-sm font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>4</span>
            </div>
            <div>
              <h3 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {lang === "fr" ? "4ème étage — Espace exclusif" : "4th floor — Exclusive space"}
              </h3>
              <p className="text-sm text-[#666666] font-light">
                {lang === "fr" ? "Étage entier, seul et privatif. Bureau unique sans voisinage." : "Entire floor, private and exclusive. Single office with no neighbours."}
              </p>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            data-testid="pricing-card-0"
            className="grid-card p-8 md:p-10 md:max-w-md"
          >
            <p className="font-mono-accent text-[#B89B72] mb-2">{lang === "fr" ? "Étage privatif" : "Private floor"}</p>
            <h4 className="text-xl md:text-2xl tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {lang === "fr" ? "Espace Privé" : "Private Space"}
            </h4>
            <p className="mt-3 text-sm text-[#666666] font-light leading-relaxed">
              {lang === "fr" ? "Étage entier dédié à votre activité. Vous êtes seul, en toute tranquillité." : "Entire floor dedicated to your business. Complete privacy and quietness."}
            </p>
            <div className="mt-8 pt-6 border-t border-black/5">
              <span className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                950€
              </span>
              <span className="text-sm text-[#999999] ml-2">/ {lang === "fr" ? "mois" : "month"}</span>
            </div>
          </motion.div>
        </div>

        {/* 3ème étage */}
        <div className="mt-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-[#B89B72] flex items-center justify-center">
              <span className="text-white text-sm font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>3</span>
            </div>
            <div>
              <h3 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {lang === "fr" ? "3ème étage — Espace partagé" : "3rd floor — Shared space"}
              </h3>
              <p className="text-sm text-[#666666] font-light">
                {lang === "fr" ? "Bureaux privatifs et coworking sur le même étage. Ambiance collaborative." : "Private offices and coworking on the same floor. Collaborative atmosphere."}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
              data-testid="pricing-card-1"
              className="grid-card p-8 md:p-10"
            >
              <p className="font-mono-accent text-[#B89B72] mb-2">{lang === "fr" ? "Bureau fermé" : "Closed office"}</p>
              <h4 className="text-xl md:text-2xl tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {lang === "fr" ? "Bureau Privatif" : "Private Office"}
              </h4>
              <p className="mt-3 text-sm text-[#666666] font-light leading-relaxed">
                {lang === "fr" ? "Votre bureau fermé au sein de l'espace partagé du 3ème étage." : "Your closed office within the shared space on the 3rd floor."}
              </p>
              <div className="mt-8 pt-6 border-t border-black/5">
                <span className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  700€
                </span>
                <span className="text-sm text-[#999999] ml-2">/ {lang === "fr" ? "mois" : "month"}</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
              data-testid="pricing-card-2"
              className="grid-card p-8 md:p-10"
            >
              <p className="font-mono-accent text-[#B89B72] mb-2">{lang === "fr" ? "Open space" : "Open space"}</p>
              <h4 className="text-xl md:text-2xl tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {lang === "fr" ? "Espace Coworking" : "Coworking Space"}
              </h4>
              <p className="mt-3 text-sm text-[#666666] font-light leading-relaxed">
                {lang === "fr" ? "Poste de travail en open space, dans une ambiance collaborative." : "Open space workstation in a collaborative atmosphere."}
              </p>
              <div className="mt-8 pt-6 border-t border-black/5">
                <span className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  350€
                </span>
                <span className="text-sm text-[#999999] ml-2">/ {lang === "fr" ? "mois" : "month"}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  const { t } = useLang();
  return (
    <section id="about" data-testid="about-section" className="section-border py-20 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <AnimatedSection>
            <motion.p variants={fadeUp} className="font-mono-accent text-[#B89B72] mb-4">{t.about.label}</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl tracking-tight">{t.about.title}</motion.h2>
          </AnimatedSection>
        </div>
        <div className="md:col-span-7">
          <AnimatedSection>
            <motion.p variants={fadeUp} className="text-[#666666] leading-relaxed font-light text-base md:text-lg">{t.about.p1}</motion.p>
            <motion.p variants={fadeUp} className="mt-6 text-[#666666] leading-relaxed font-light text-base md:text-lg">{t.about.p2}</motion.p>
            <motion.div variants={fadeUp} className="mt-8 grid grid-cols-3 gap-6">
              {t.about.stats.map((stat) => (
                <div key={stat.label}>
                  <p className="text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>{stat.num}</p>
                  <p className="font-mono-accent text-[#999999] mt-1">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

function LocationSection() {
  const { t } = useLang();
  return (
    <section id="location" data-testid="location-section" className="section-border py-20 md:py-32 px-6 md:px-12 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto">
        <AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <motion.p variants={fadeUp} className="font-mono-accent text-[#B89B72] mb-4">{t.location.label}</motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl tracking-tight">{t.location.title}</motion.h2>
              <motion.div variants={fadeUp} className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin size={20} strokeWidth={1.5} className="text-[#B89B72] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{t.location.address}</p>
                    <p className="text-[#666666] font-light">{t.location.city}</p>
                    <p className="text-[#666666] font-light">{t.location.floor}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone size={20} strokeWidth={1.5} className="text-[#B89B72] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{t.location.phone}</p>
                    <p className="text-[#666666] font-light">{t.location.hours}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail size={20} strokeWidth={1.5} className="text-[#B89B72] mt-1 flex-shrink-0" />
                  <div><p className="font-medium">{t.location.email}</p></div>
                </div>
              </motion.div>
            </div>
            <div className="md:col-span-7">
              <motion.div variants={fadeUp} className="map-container w-full h-[350px] md:h-[420px] flex items-center justify-center overflow-hidden">
                <iframe data-testid="location-map" title="Crown Workspace Luxembourg" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2585.5!2d6.05!3d49.62!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s321+Route+d'Arlon+Strassen+Luxembourg!5e0!3m2!1sen!2slu!4v1" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </motion.div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

function ContactSection() {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", needs_accounting: "", message: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", company: "", service: "", needs_accounting: "", message: "" });
    } catch {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-border py-20 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <AnimatedSection>
            <motion.p variants={fadeUp} className="font-mono-accent text-[#B89B72] mb-4">{t.contact.label}</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl tracking-tight">{t.contact.title}</motion.h2>
            <motion.p variants={fadeUp} className="mt-4 text-[#666666] leading-relaxed font-light">{t.contact.subtitle}</motion.p>
          </AnimatedSection>
        </div>
        <div className="md:col-span-7">
          <form data-testid="contact-form" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="font-mono-accent text-[#999999] block mb-2">{t.contact.nameLabel}</label>
                <input data-testid="contact-name-input" type="text" name="name" required value={form.name} onChange={handleChange} placeholder={t.contact.namePlaceholder} className="form-input-sharp" />
              </div>
              <div>
                <label className="font-mono-accent text-[#999999] block mb-2">{t.contact.emailLabel}</label>
                <input data-testid="contact-email-input" type="email" name="email" required value={form.email} onChange={handleChange} placeholder={t.contact.emailPlaceholder} className="form-input-sharp" />
              </div>
              <div>
                <label className="font-mono-accent text-[#999999] block mb-2">{t.contact.phoneLabel}</label>
                <input data-testid="contact-phone-input" type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder={t.contact.phonePlaceholder} className="form-input-sharp" />
              </div>
              <div>
                <label className="font-mono-accent text-[#999999] block mb-2">{t.contact.companyLabel}</label>
                <input data-testid="contact-company-input" type="text" name="company" value={form.company} onChange={handleChange} placeholder={t.contact.companyPlaceholder} className="form-input-sharp" />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-accent text-[#999999] block mb-2">{t.contact.serviceLabel}</label>
                <select data-testid="contact-service-select" name="service" value={form.service} onChange={handleChange} className="form-input-sharp bg-white">
                  <option value="">{t.contact.serviceDefault}</option>
                  {Object.entries(t.contact.serviceOptions).map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-accent text-[#999999] block mb-2">{t.contact.accountingLabel}</label>
                <p className="text-xs text-[#999999] font-light mb-3">{t.contact.accountingDesc}</p>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(t.contact.accountingOptions).map(([value, label]) => (
                    <button key={value} type="button" data-testid={`accounting-option-${value}`} onClick={() => setForm((prev) => ({ ...prev, needs_accounting: value }))}
                      className={`px-5 py-3 text-sm transition-all duration-300 ${form.needs_accounting === value ? "bg-[#111111] text-white border border-[#111111]" : "bg-transparent text-[#666666] border border-black/15 hover:border-black/30"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                {form.needs_accounting === "oui" && (
                  <div data-testid="accounting-redirect" className="mt-4 p-4 border border-[#B89B72]/30 bg-[#FAFAFA]">
                    <p className="text-sm text-[#333333]">
                      {lang === "fr"
                        ? "Notre cabinet comptable partenaire se fera un plaisir de vous accompagner. Contactez-les directement :"
                        : "Our partner accounting firm will be happy to assist you. Contact them directly:"}
                    </p>
                    <a href="mailto:secretariat@fhgestion.lu" className="inline-flex items-center gap-2 mt-2 text-sm font-medium text-[#B89B72] hover:text-[#111111] transition-colors">
                      <Mail size={14} strokeWidth={1.5} /> secretariat@fhgestion.lu
                    </a>
                  </div>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-accent text-[#999999] block mb-2">{t.contact.messageLabel}</label>
                <textarea data-testid="contact-message-input" name="message" required rows={5} value={form.message} onChange={handleChange} placeholder={t.contact.messagePlaceholder} className="form-input-sharp resize-none" />
              </div>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button data-testid="contact-submit-button" type="submit" disabled={loading} className="btn-sharp flex items-center gap-3">
                {loading ? t.contact.sending : t.contact.submit}
                {!loading && <ArrowRight size={14} strokeWidth={1.5} />}
              </button>
              {status === "success" && <p data-testid="contact-success-message" className="text-green-700 font-mono-accent">{t.contact.success}</p>}
              {status === "error" && <p data-testid="contact-error-message" className="text-red-700 font-mono-accent">{t.contact.error}</p>}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const { lang, t } = useLang();
  return (
    <section id="testimonials" data-testid="testimonials-section" className="section-border py-20 md:py-32 px-6 md:px-12 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto">
        <AnimatedSection>
          <motion.p variants={fadeUp} className="font-mono-accent text-[#B89B72] mb-4">{t.testimonials.label}</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl tracking-tight max-w-2xl">{t.testimonials.title}</motion.h2>
        </AnimatedSection>
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((item, i) => (
            <motion.div key={i} data-testid={`testimonial-card-${i}`} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, delay: i * 0.15, ease: "easeOut" }} className="grid-card p-8 md:p-10 bg-white flex flex-col justify-between">
              <div>
                <Quote size={28} strokeWidth={1} className="text-[#B89B72] mb-6" />
                <p className="text-lg md:text-xl leading-relaxed tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  &laquo;&nbsp;{lang === "fr" ? item.text_fr : item.text_en}&nbsp;&raquo;
                </p>
              </div>
              <div className="mt-8 pt-6 border-t border-black/5 flex items-center gap-4">
                <img src={item.avatar} alt={item.author} className="w-10 h-10 rounded-full border border-black/10" />
                <div>
                  <p className="font-medium text-sm">{item.author}</p>
                  <p className="font-mono-accent text-[#999999] mt-0.5">{item.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useLang();
  const navIds = ["services", "about", "location", "contact"];
  return (
    <footer data-testid="footer" className="section-border bg-[#111111] text-white py-16 md:py-20 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <h3 className="text-2xl tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Crown Workspace</h3>
            <p className="font-mono-accent text-[#B89B72] mt-1">Luxembourg</p>
            <p className="mt-6 text-white/50 font-light leading-relaxed text-sm">{t.footer.tagline}</p>
          </div>
          <div className="md:col-span-3 md:col-start-7">
            <p className="font-mono-accent text-white/30 mb-4">{t.footer.navTitle}</p>
            <div className="space-y-3">
              {t.footer.navLinks.map((label, i) => (
                <button key={label} data-testid={`footer-link-${label.toLowerCase()}`} onClick={() => document.getElementById(navIds[i])?.scrollIntoView({ behavior: "smooth" })} className="block text-white/50 hover:text-white transition-colors duration-300 text-sm font-light">
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-3">
            <p className="font-mono-accent text-white/30 mb-4">{t.footer.addressTitle}</p>
            <p className="text-white/50 text-sm font-light leading-relaxed">
              321, Route d'Arlon<br />L-8011 Strassen<br />Luxembourg<br />{t.location.floor}
            </p>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-white/30 text-xs font-light">{t.footer.copyright.replace("{year}", new Date().getFullYear())}</p>
          <p className="font-mono-accent text-white/20 text-[0.6rem]">Designed with elegance</p>
        </div>
      </div>
    </footer>
  );
}

function WhatsAppButton() {
  const { lang } = useLang();
  const message = lang === "fr"
    ? "Bonjour, je suis int\u00e9ress\u00e9(e) par vos espaces de travail \u00e0 Strassen."
    : "Hello, I'm interested in your workspaces in Strassen.";
  const url = `https://wa.me/352621572444?text=${encodeURIComponent(message)}`;

  return (
    <a
      data-testid="whatsapp-button"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300"
      aria-label="WhatsApp"
    >
      <svg viewBox="0 0 32 32" width="28" height="28" fill="white">
        <path d="M16.004 0C7.165 0 .002 7.163.002 16c0 2.822.736 5.573 2.137 7.998L.014 32l8.204-2.1A15.93 15.93 0 0016.004 32C24.838 32 32 24.837 32 16S24.838 0 16.004 0zm0 29.39a13.36 13.36 0 01-7.215-2.1l-.518-.307-4.868 1.248 1.3-4.733-.34-.537A13.32 13.32 0 012.62 16c0-7.384 6.01-13.39 13.384-13.39S29.39 8.616 29.39 16c0 7.384-6.01 13.39-13.386 13.39zm7.338-10.022c-.402-.2-2.38-1.174-2.75-1.308-.37-.134-.638-.2-.907.201-.268.4-1.04 1.308-1.275 1.576-.235.268-.47.302-.872.1-.402-.2-1.697-.625-3.232-1.993-1.195-1.065-2-2.38-2.235-2.782-.235-.402-.025-.618.177-.818.181-.18.402-.47.603-.704.201-.235.268-.402.402-.67.134-.268.067-.502-.034-.703-.1-.2-.907-2.184-1.242-2.99-.327-.784-.66-.678-.907-.69-.235-.012-.502-.014-.77-.014s-.703.1-1.072.502c-.37.4-1.41 1.375-1.41 3.36s1.443 3.895 1.644 4.163c.2.268 2.84 4.334 6.882 6.076.962.415 1.713.663 2.299.85.966.307 1.845.264 2.54.16.775-.116 2.38-.973 2.716-1.912.335-.94.335-1.744.235-1.912-.1-.168-.37-.268-.77-.47z"/>
      </svg>
    </a>
  );
}

function App() {
  const [lang, setLang] = useState("fr");
  const toggle = () => setLang((l) => (l === "fr" ? "en" : "fr"));
  const t = translations[lang];

  return (
    <LangContext.Provider value={{ lang, t, toggle }}>
      <div data-testid="app-root">
        <Navigation />
        <HeroSection />
        <MarqueeRibbon />
        <ServicesSection />
        <FeaturesStrip />
        <PricingSection />
        <AboutSection />
        <LocationSection />
        <ContactSection />
        <TestimonialsSection />
        <Footer />
        <WhatsAppButton />
      </div>
    </LangContext.Provider>
  );
}

export default App;
